import { PifSubFlow } from "../../../types/pifDto";

export type PifScreen = "start" | "form" | "done";
export type PifFlow = "default" | "new" | "active" | "inactive";
export type PifHasLicense = "" | "yes" | "no";
export type PifYesNo = "yes" | "no";
export type PifSectionId = "applicant" | "contact" | "position" | "pay" | "details";

/**
 * Answers to the dynamic (EAV) fields, keyed by `fieldKey`.
 *
 * Kept separate from PifFormValues because the catalogue is data — the API decides which
 * fields exist, so they cannot be a fixed TypeScript shape.
 */
/**
 * EAV field keys whose value is captured by a BESPOKE control in the Position / Pay cards,
 * not by PifSectionDynamic. Storage is EAV; rendering stays bespoke - the two are independent.
 *
 * Single source of truth for four call sites, which must stay in agreement:
 *   1. PifWorkspace.persistDraft  - forward bridge, f -> eav on save
 *   2. PostInterviewForm reopen   - reverse bridge, eav -> f on load
 *   3. PifSectionDynamic          - EXCLUDES these, or the field renders twice
 *   4. getPifEavGaps              - EXCLUDES these, or the field is counted twice as missing
 *
 * Keys ending in an id (rate_type) store the reference id, never the display label, so the
 * value stays joinable by downstream consumers.
 */
export const PIF_BRIDGED_FIELD_KEYS = [
  "requested_rate",
  "desired_start_date",
  "department_notes",
  "hours_per_week",
  "rate_type",
  "service",
  "client",
  "reports_to",
  // Epic 79822 seeded this attribute for the list grid's Type column and its script states the SPA
  // bridges it — but it was never added here, so the FT/PT toggle in the Position card saved
  // nothing, and the catalogue entry instead surfaced as a SECOND, free-text "Employment Type" box
  // in Additional details (the only one whose value reached save-answers). Stores the label, not an
  // id: the attribute's DataType is 'text'.
  "employment_type",
  // Same omission as employment_type above: the driving questions in the Contact and Pay cards
  // were rendered and validated but had no persistence path at all - not in save-applicant's
  // payload and not bridged - so "Holds a driver's license", the licence number and "Will operate
  // an agency vehicle" were lost on every reload. All three store the plain control value
  // ('yes'/'no', or the number as typed); the attributes are DataType 'text'.
  "has_license",
  "license_number",
  "will_operate_vehicle",
] as const;

/**
 * A Select placeholder is <MenuItem value="">, and Number("") is 0 - a bogus id that reads as
 * "chosen" everywhere downstream. Empty must become null, not 0.
 */
export const toSelectId = (value: string | number): number | null =>
  value === "" ? null : Number(value);

/**
 * Keeps only digits and a single decimal point, so letters and symbols cannot be typed or pasted
 * into a money/hours field at all. inputMode="decimal" is only a soft keyboard hint on mobile and
 * does nothing on desktop, which is how "$25" reached the API and killed the whole save batch.
 */
export const toDecimalInput = (value: string): string => {
  const cleaned = String(value ?? "").replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  return parts.length > 1 ? parts[0] + "." + parts.slice(1).join("") : parts[0];
};

export const isBridgedFieldKey = (fieldKey: string): boolean =>
  (PIF_BRIDGED_FIELD_KEYS as readonly string[]).includes(fieldKey);

/**
 * EAV field keys whose generic text input accepts DIGITS ONLY (PifSectionDynamic strips anything
 * else as it is typed or pasted).
 *
 * Declared here rather than special-cased inside the renderer so the list stays visible next to
 * PIF_BRIDGED_FIELD_KEYS and adding a field needs no component change.
 *
 * These stay DataType 'text', deliberately NOT 'decimal': decimal is a quantity, and it would give
 * the field a "0.00" placeholder, accept a decimal point, drag in the MinValue/MaxValue range hint,
 * and let TRY_CONVERT strip a leading zero. A phone number is a digit string, not a number you can
 * do arithmetic on.
 */
export const PIF_DIGITS_ONLY_FIELD_KEYS = ["emergency_contact_number"] as const;

export const isDigitsOnlyFieldKey = (fieldKey: string): boolean =>
  (PIF_DIGITS_ONLY_FIELD_KEYS as readonly string[]).includes(fieldKey);

/** Everything that is not a digit, dropped. Shared so the input and its tests agree. */
export const toDigitsOnly = (value: string): string => (value || "").replace(/\D/g, "");

export type PifEavValues = Record<string, string>;

/**
 * The saved answers a reopened draft may put into `eav` state — i.e. the generic
 * "Additional details" ones only.
 *
 * A bridged key is owned by `f` (a hardcoded control in the Position/Pay cards) and is EXCLUDED
 * from PifSectionDynamic, so the user cannot reach it through the dynamic section. Seeding one
 * into `eav` is therefore never useful and is actively harmful: PifWorkspace.persistDraft merges
 * `{ ...derived, ...eav }` so that a deliberate dynamic-section answer wins, which means a stale
 * `eav` copy of a bridged key overrode the value the user had just picked — every edit to Reports
 * to / Service / Hours per week / Rate type on a REOPENED draft was silently discarded and the
 * originally-saved value written back unchanged.
 */
export const omitBridgedFieldKeys = (answers: PifEavValues): PifEavValues =>
  Object.fromEntries(Object.entries(answers).filter(([fieldKey]) => !isBridgedFieldKey(fieldKey)));

// The classification value the API's System-Departments-Positions-RoleType setting uses for a
// community DSP position. It gates the service/client fields and forces part-time. Null under the
// interim fallback (setting not seeded), in which case the community-DSP flow stays off.
export const COMMUNITY_DSP_CLASSIFICATION = "Community DSP";

export interface PifPastPosition {
  pos: string;
  dept: string;
  dates: string;
  sup: string;
  ext: string;
  active: boolean;
}

/**
 * A person matched by POST api/postif/identity-lookup.
 *
 * Deliberately carries no SSN — not the raw value and not the API's `maskedSsn`
 * (which holds the last four). Epic 79820 requires the SSN is never shown, and
 * keeping it out of component state means it cannot leak into a render or a log.
 */
export interface PifCandidate {
  status: PifSubFlow;
  /** Enterprise HR_Employees id. Feeds `enterpriseEmployeeId` on save-applicant. */
  employeeId?: number;
  first?: string;
  middle?: string;
  last?: string;
  dob?: string;
  addr1?: string;
  city?: string;
  state?: string;
  zip?: string;
  home?: string;
  cell?: string;
  email?: string;
  /** Not supplied by identity-lookup — the Avatar falls back to initials. */
  profileImage?: string;
  /** Populated from GET employment-history (task 7), not from the lookup. */
  positions?: PifPastPosition[];
}

/** The lookup outcome, already reduced to the three states the start screen renders. */
export type PifLookupResult =
  | {
      kind: "match";
      flow: PifSubFlow;
      candidate: PifCandidate;
      /** An open draft this user already has for the person — resume it instead of creating one. */
      existingDraftPostIfId?: number | null;
      existingDraftRefNo?: string | null;
    }
  | { kind: "new-applicant" }
  | { kind: "blocked"; message: string };

/** The open draft: created once the person is confirmed, and record-locked by the API. */
export interface PifDraft {
  /**
   * Null until the first save. The Portal_PostIF row is written by usePifDraft.ensureCreated on
   * the first save-applicant/save-answers, not when the form opens - see that hook's header.
   */
  postIfId: number | null;
  refNo: string | null;
  /** fieldKey → postIfAttributeId, the only way to address an EAV answer on save. */
  attributeIdByFieldKey: Record<string, number>;
}

export interface PifFormValues {
  q: string;
  firstName: string;
  middleName: string;
  lastName: string;
  knownAs: string;
  dob: string;
  addr1: string;
  city: string;
  state: string;
  zip: string;
  home: string;
  cell: string;
  email: string;
  hasLicense: PifHasLicense;
  licNum: string;
  hireType: string;
  startDate: string;
  empType: "Part-time" | "Full-time";
  hours: string;
  rate: string;
  vehicle: PifYesNo;
  notes: string;
  // Reference selections carry the API id for the calls and the text for display, because
  // the offer rail and review drawer render a summary without the lookup lists to hand.
  deptId: number | null;
  dept: string;
  /** Doubles as the header's DeptPositionId — position-rules keys off it. */
  posId: number | null;
  pos: string;
  reportsToId: number | null;
  reportsTo: string;
  serviceId: number | null;
  service: string;
  clientId: number | null;
  client: string;
  rateTypeId: number | null;
  rateType: string;
}

export const PIF_BLANK_FORM: PifFormValues = {
  q: "",
  firstName: "",
  middleName: "",
  lastName: "",
  knownAs: "",
  dob: "",
  addr1: "",
  city: "",
  state: "NY",
  zip: "",
  home: "",
  cell: "",
  email: "",
  hasLicense: "",
  licNum: "",
  hireType: "",
  startDate: "",
  empType: "Part-time",
  hours: "",
  rate: "",
  vehicle: "no",
  notes: "",
  deptId: null,
  dept: "",
  posId: null,
  pos: "",
  reportsToId: null,
  reportsTo: "",
  serviceId: null,
  service: "",
  clientId: null,
  client: "",
  rateTypeId: null,
  rateType: "",
};

// Internal position "meta" shape the approval assembler reads (band/hiring bounds + classification),
// adapted from the API's position-rules by toPifPositionMeta in pifListApproval.
export interface PifPositionMeta {
  cls: string;
  office: boolean;
  sched: boolean;
  band: [number, number];
  hiring: [number, number];
}

export interface PifGap {
  label: string;
  sec: PifSectionId;
  /**
   * DOM id of the control that answers it (pifFocus.pifFieldId / pifEavFieldId), so clicking the
   * error can focus that field rather than only scrolling to its section.
   */
  field: string;
}

export interface PifSummaryRow {
  k: string;
  v: string;
}

/* ── PIF Approval (Department Director / HR Director / Executive Director sign-off) ── */

export type PifApproverRole = "dept" | "hr" | "exec";
export type PifApprovalOutcome = "signed" | "changes" | "declined";
export type PifApprovalChainState = "done" | "current" | "waiting";
export type PifApprovalPillTone = "warn" | "ok";

export interface PifApprovalRoleMeta {
  label: string;
  order: number;
  signCta: string;
  forward: string | null;
  receiptNext: string;
}

export interface PifApprovalChainEntry {
  name: string;
  role: string;
  detail: string;
  stamp: string;
  state: PifApprovalChainState;
}

export interface PifApprovalAuditEntry {
  what: string;
  when: string;
}

export interface PifApprovalDisplayRow {
  k: string;
  v: string;
  pill?: string;
  pillTone?: PifApprovalPillTone;
  hint?: string;
}

export interface PifApprovalBand {
  positionMin: number;
  positionMax: number;
  hiringMin: number;
  hiringMax: number;
  rate: number;
  outOfBand: boolean;
}

export interface PifApprovalSection {
  title: string;
  note: string;
  rows: PifApprovalDisplayRow[];
  band?: PifApprovalBand;
}

export interface PifApprovalRecord {
  // Numeric PostIF id for the approve/reject calls; pifId below is the display ref (PIF-YYYY-####).
  postIfId: number;
  pifId: string;
  currentRole: PifApproverRole;
  applicantName: string;
  applicantInitials: string;
  profileImage?: string;
  positionTag: string;
  applicantMeta: string;
  /**
   * The record's own status ("submitted", "approved", …) and nothing else. It used to be a
   * "PIF-2026-0232 · submitted" header line, but the page band above the review already carries
   * both the ref and the page title, so the ref was printed three times in one screen height
   * (Epic 79820). Rendered as the leading chip in the review topbar.
   */
  statusLabel: string;
  employeeIdLabel: string;
  // Deep-link expiry countdown — only present when the API supplies it (not on the current read).
  expiresAt?: string;
  viewerIsCurrentSigner: boolean;
  // The viewer created/supervises this PIF (header.createdByUserId === logged-in userId). One of the
  // two grants for the read-only "Resend sign link" action (see viewerHasApproved).
  viewerIsCreator: boolean;
  // The viewer has ALREADY approved this PIF (a signed signature row is theirs). Also gates "Resend
  // sign link", so any approver who signed can re-nudge the current pending signer — not just the
  // creator. The server re-enforces this in sProc_Portal_PostIF_CanAct_S ('resend'), so this is UX only.
  viewerHasApproved: boolean;
  chain: PifApprovalChainEntry[];
  audit: PifApprovalAuditEntry[];
  sections: PifApprovalSection[];
}

export interface PifApprovalActionResult {
  pifId: string;
  signedAt: string;
}

export interface PifRejectPayload {
  id: string;
  note: string;
}

/* ── PIF list — hiring supervisor vs HR columns; static stub until the list API exists ── */

export type PifListStatus =
  | "DRAFT"
  // Returned by an approver for changes: still the supervisor's to edit and re-submit, which
  // is why it must NOT collapse into DECLINED - every "is this editable" check keys off this.
  | "RETURNED"
  | "PENDING DEPT DIR"
  | "PENDING HR DIR"
  | "PENDING EXEC DIR"
  | "APPROVED"
  | "DECLINED";

/**
 * The list statuses whose row is still the creator's to open and edit - drives the Edit/Delete
 * actions and whether "Open" routes to /:id/edit or to the read-only /:id approval screen.
 */
export const EDITABLE_LIST_STATUSES: PifListStatus[] = ["DRAFT", "RETURNED"];

/**
 * Portal_PostIF.Status values that reopen as an EDITABLE form, mirroring exactly what the API
 * accepts: sProc_Portal_PostIFAttributeValues_IU, sProc_Portal_PostIFApplicants_IU and
 * sProc_Portal_PostIF_Submit_U all allow 'draft' and 'changes_requested'. Lowercase, as the API
 * returns them.
 */
export const REOPENABLE_PIF_STATUSES = ["draft", "changes_requested"];

export type PifListFlow = "New" | "Existing·Active" | "Existing·Inactive";

export type PifListEmploymentType = "FT" | "PT";

/**
 * A list-row pipeline dot. "returned" is deliberately distinct from "declined": the chain stopped
 * at that stage either way, but a returned form is still the supervisor's to fix and re-submit
 * while a declined one is dead. Same reasoning as PifListStatus' "RETURNED".
 */
export type PifApprovalStepState = "signed" | "pending" | "declined" | "returned";

export interface PifApprovalStep {
  label: string;
  state: PifApprovalStepState;
  detail: string;
}

export interface PifListItem {
  id: string;
  title: string;
  flow: PifListFlow;
  applicant: string;
  createdBy: string;
  createdByUserId?: number;
  department: string;
  position: string;
  employmentType: PifListEmploymentType;
  status: PifListStatus;
  updated: string;
  /** Display form, "#1234". Present for an existing employee from draft stage, not only after
   *  conversion - so it must NOT be used to decide whether the row has been converted. */
  employeeNumber?: string;
  /** The raw id behind employeeNumber, so Mark-as-employee can prefill instead of asking HR to
   *  retype a number the record already holds (a typo there would overwrite a correct one). */
  enterpriseEmployeeId?: number;
  /**
   * HR has converted this PostIF into a hire (Portal_PostIF.MarkedAsEmployeeAt is set). This - not
   * employeeNumber - is what gates the Mark-as-employee action: since the employee id is recorded
   * at draft save for an existing employee, keying off the number would hide the action on exactly
   * the rows that still need it.
   */
  markedAsEmployee?: boolean;
  approvals: PifApprovalStep[];
  isMine: boolean;
}
